// services/playbackPosition.js
// Server-backed cross-device "Continue Watching" state.
//
// Architecture:
//   - In-memory Map is the source of truth for synchronous reads
//     (getPosition / getAllPositions). It's hydrated on the first
//     hydrate() call after auth lands.
//   - Writes go to the server via fire-and-forget fetch and ALSO
//     update the in-memory cache so subsequent reads in the same
//     session feel instant.
//   - localStorage is a write-through offline cache so the rail still
//     shows something useful before the hydrate() POST returns, and
//     so deleted positions don't reappear on a network failure.
//
// Storage shape:
//   - Server: (invite_token_id, source, file_key, position, duration,
//     updated_at). Keyed per invite, so two devices on the same invite
//     share the state -- the headline cross-device feature.
//   - Client cache key: `${source}:${fileKey}`. Default source is
//     'putio' so legacy callers that pass a bare numeric file id still
//     work.
//
// Sources currently supported:
//   putio, torbox, youtube, plex, jellyfin, live -- the same set the
//   server `playback_positions` table CHECK constraint enforces.

const MIN_POSITION_SECONDS = 10;
const COMPLETION_THRESHOLD = 0.95;

// In-memory cache. Keys are `${source}:${fileKey}`. Values are
// { source, fileKey, position, duration, updatedAt }.
const cache = new Map();

// Local-storage mirror of the cache so a page refresh while offline
// (or during the hydrate() round-trip) doesn't lose what the rail
// just showed.
const STORAGE_KEY = "putio_playback_cache_v2";

let hydrated = false;
let hydratePromise = null;

function cacheKey(source, fileKey) {
  return `${source}:${fileKey}`;
}

// Parse a legacy bare-id-string callsite into { source, fileKey }.
// Old callers pass `savePosition(fileId, position, duration)` -- treat
// the numeric/string id as a put.io fileKey. New callers can pass an
// object `{ source, fileKey }` for any source.
function resolveTarget(fileIdOrTarget) {
  if (fileIdOrTarget == null) return null;
  if (
    typeof fileIdOrTarget === "object" &&
    typeof fileIdOrTarget.source === "string" &&
    fileIdOrTarget.fileKey != null
  ) {
    const source = String(fileIdOrTarget.source);
    const fileKey = String(fileIdOrTarget.fileKey);
    if (!source || !fileKey) return null;
    // Optional renderable metadata (v32) — lets the Continue Watching rail
    // build a card for non-Put.io sources (torbox/plex/jellyfin) whose keys
    // carry no title/poster hook. Persisted server-side; the rail links to
    // `resumePath` (a same-origin "/…" route the player navigated to).
    const meta = {};
    if (typeof fileIdOrTarget.title === "string")
      meta.title = fileIdOrTarget.title;
    if (typeof fileIdOrTarget.poster === "string")
      meta.poster = fileIdOrTarget.poster;
    if (typeof fileIdOrTarget.resumePath === "string")
      meta.resumePath = fileIdOrTarget.resumePath;
    // Episode context (v33): show title + season/episode so the rail can render
    // "S1·E1 · <episode>" and match the SHOW for a poster.
    if (typeof fileIdOrTarget.seriesTitle === "string")
      meta.seriesTitle = fileIdOrTarget.seriesTitle;
    if (
      fileIdOrTarget.season != null &&
      Number.isFinite(Number(fileIdOrTarget.season))
    )
      meta.season = Number(fileIdOrTarget.season);
    if (
      fileIdOrTarget.episode != null &&
      Number.isFinite(Number(fileIdOrTarget.episode))
    )
      meta.episode = Number(fileIdOrTarget.episode);
    return { source, fileKey, ...meta };
  }
  const fileKey = String(fileIdOrTarget);
  if (!fileKey) return null;
  return { source: "putio", fileKey };
}

function readLocalCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    for (const entry of parsed) {
      if (!entry || typeof entry !== "object") continue;
      if (!entry.source || !entry.fileKey) continue;
      cache.set(cacheKey(entry.source, entry.fileKey), {
        source: entry.source,
        fileKey: entry.fileKey,
        position: Number(entry.position) || 0,
        duration: Number(entry.duration) || 0,
        updatedAt: entry.updatedAt || new Date().toISOString(),
        title: entry.title ?? null,
        poster: entry.poster ?? null,
        resumePath: entry.resumePath ?? null,
        seriesTitle: entry.seriesTitle ?? null,
        season: entry.season ?? null,
        episode: entry.episode ?? null,
      });
    }
  } catch {
    // Malformed cache -- ignore. Server hydrate() will replace it.
  }
}

function writeLocalCache() {
  try {
    const arr = Array.from(cache.values());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch {
    // Quota / private-mode failures aren't fatal -- the server is
    // still authoritative.
  }
}

// One-time prime from localStorage so the rail has something to show
// before hydrate() finishes.
readLocalCache();

// savePosition fires every ~5s during playback; stringifying the ENTIRE cache
// + a synchronous setItem on each tick is main-thread work that buys nothing
// (the server write is the durable one — this mirror only needs to be roughly
// fresh). Throttle the mirror to one write per 30s...
let lastLocalCacheWrite = 0;
// ...and flush on pagehide so the final position of a session isn't lost to
// the throttle window. (pagehide fires on tab close, navigation AND bfcache
// eviction — unlike unload it's reliable on mobile.)
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => writeLocalCache());
}

/**
 * Fetch every position for the calling invite from the server and
 * replace the in-memory cache. Safe to call multiple times -- only
 * the first call actually hits the wire; subsequent calls return the
 * same in-flight promise.
 */
export async function hydrate() {
  if (hydrated) return;
  if (hydratePromise) return hydratePromise;

  hydratePromise = (async () => {
    try {
      const res = await fetch("/api/playback", {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        // 401 (locked) is expected on the unlock page and other public
        // routes. Don't trash the cache -- it might still have useful
        // entries from the previous unlocked session.
        return;
      }
      const body = await res.json();
      const items = Array.isArray(body?.items) ? body.items : [];
      cache.clear();
      for (const row of items) {
        if (!row || !row.source || !row.file_key) continue;
        cache.set(cacheKey(row.source, row.file_key), {
          source: row.source,
          fileKey: String(row.file_key),
          position: Number(row.position_seconds) || 0,
          duration: Number(row.duration_seconds) || 0,
          updatedAt: row.updated_at
            ? new Date(row.updated_at).toISOString()
            : new Date().toISOString(),
          // Renderable metadata (v32) — null on rows saved before the upgrade.
          title: row.title ?? null,
          poster: row.poster_url ?? null,
          resumePath: row.resume_path ?? null,
          // Episode context (v33).
          seriesTitle: row.series_title ?? null,
          season: row.season ?? null,
          episode: row.episode ?? null,
        });
      }
      writeLocalCache();
      hydrated = true;
    } catch {
      // Network / DNS failure -- keep the localStorage cache as a
      // best-effort fallback. We'll retry the next time the app boots.
    } finally {
      hydratePromise = null;
    }
  })();
  return hydratePromise;
}

/**
 * Save the current playback position. Updates the cache synchronously
 * (so the rail reflects it immediately) and fires a PUT to the server
 * asynchronously. Returns nothing.
 *
 * Accepts either a legacy bare fileId (defaulting source='putio') or
 * an explicit { source, fileKey } target.
 */
export function savePosition(fileIdOrTarget, position, duration) {
  const t = resolveTarget(fileIdOrTarget);
  if (!t) return;
  const pos = Number(position);
  const dur = Number(duration);
  if (!Number.isFinite(pos) || !Number.isFinite(dur) || dur <= 0) return;

  // Auto-finish.
  if (pos / dur > COMPLETION_THRESHOLD) {
    clearPosition(fileIdOrTarget);
    return;
  }
  // Below threshold -- ignore.
  if (pos < MIN_POSITION_SECONDS) return;

  const key = cacheKey(t.source, t.fileKey);
  const prev = cache.get(key);
  cache.set(key, {
    source: t.source,
    fileKey: t.fileKey,
    position: pos,
    duration: dur,
    updatedAt: new Date().toISOString(),
    // Renderable metadata (v32). Carry it forward when this tick omits it
    // (a position-only timeupdate), mirroring the server-side COALESCE.
    title: t.title ?? prev?.title ?? null,
    poster: t.poster ?? prev?.poster ?? null,
    resumePath: t.resumePath ?? prev?.resumePath ?? null,
    seriesTitle: t.seriesTitle ?? prev?.seriesTitle ?? null,
    season: t.season ?? prev?.season ?? null,
    episode: t.episode ?? prev?.episode ?? null,
  });
  // Throttled mirror write (see lastLocalCacheWrite above). clearPosition /
  // hydrate keep their immediate writes; pagehide flushes the tail.
  if (Date.now() - lastLocalCacheWrite > 30000) {
    writeLocalCache();
    lastLocalCacheWrite = Date.now();
  }

  // Fire-and-forget push to the server.
  pushSave(t, pos, dur);
}

function pushSave(target, position, duration) {
  const body = { position, duration };
  // Only send metadata when this call carries it; the server COALESCEs so a
  // position-only tick won't wipe a previously-saved title/poster/path.
  if (typeof target.title === "string") body.title = target.title;
  if (typeof target.poster === "string") body.poster = target.poster;
  if (typeof target.resumePath === "string")
    body.resumePath = target.resumePath;
  if (typeof target.seriesTitle === "string")
    body.seriesTitle = target.seriesTitle;
  if (target.season != null) body.season = target.season;
  if (target.episode != null) body.episode = target.episode;
  try {
    fetch(
      `/api/playback/${encodeURIComponent(target.source)}/${encodeURIComponent(target.fileKey)}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      },
    ).catch(() => {
      /* network error -- local cache keeps the value */
    });
  } catch {
    /* total fetch failure -- local cache keeps the value */
  }
}

/**
 * Get the saved playback position. Synchronous read from the cache.
 * Returns { position, duration, updatedAt, source, fileKey } or null.
 */
export function getPosition(fileIdOrTarget) {
  const t = resolveTarget(fileIdOrTarget);
  if (!t) return null;
  return cache.get(cacheKey(t.source, t.fileKey)) || null;
}

/**
 * Delete the saved playback position.
 */
export function clearPosition(fileIdOrTarget) {
  const t = resolveTarget(fileIdOrTarget);
  if (!t) return;
  cache.delete(cacheKey(t.source, t.fileKey));
  writeLocalCache();

  try {
    fetch(
      `/api/playback/${encodeURIComponent(t.source)}/${encodeURIComponent(t.fileKey)}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: { Accept: "application/json" },
      },
    ).catch(() => {
      /* nbd -- cache already updated */
    });
  } catch {
    /* nbd */
  }
}

/**
 * Get all saved playback positions for the calling invite (NOT the
 * device). Used by the Continue Watching rail. Returns a Map keyed by
 * fileKey for backward compat with the previous device-local API; new
 * callers can use `getAllPositionsBySource()` to disambiguate sources.
 */
export function getAllPositions() {
  const out = new Map();
  for (const entry of cache.values()) {
    out.set(entry.fileKey, entry);
  }
  return out;
}

/**
 * Get all positions grouped by source. Use this when the caller cares
 * about source disambiguation (e.g. a unified Continue Watching rail
 * that mixes put.io / plex / jellyfin items).
 */
export function getAllPositionsBySource() {
  const bySource = new Map();
  for (const entry of cache.values()) {
    let bucket = bySource.get(entry.source);
    if (!bucket) {
      bucket = new Map();
      bySource.set(entry.source, bucket);
    }
    bucket.set(entry.fileKey, entry);
  }
  return bySource;
}

// -----------------------------------------------------------------------------
// Legacy compat — kept so existing callers compile.
// -----------------------------------------------------------------------------

// cleanExpired() was the device-local localStorage cleaner. The server
// owns retention now (the repo prunes old rows on the next admin
// pass); we keep this export as a no-op so the existing call sites
// don't need to be touched in this commit.
export function cleanExpired() {
  /* server owns retention */
}

// Test hook: reset the in-memory cache between tests.
export function __resetForTest() {
  cache.clear();
  hydrated = false;
  hydratePromise = null;
}
